import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SignedImage from "@/components/media/SignedImage";
import { formatDistanceToNow } from "date-fns";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BlockButton from "@/components/safety/BlockButton";
import ReportDialog from "@/components/safety/ReportDialog";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useConversations } from "@/lib/messaging/useConversations";
import { useMessages } from "@/lib/messaging/useMessages";
import { humanizeSendError } from "@/lib/messaging/sendErrors";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, MoreVertical, Send, Ban, Flag } from "lucide-react";
import RaelaCoPilotButton from "@/components/messaging/RaelaCoPilotButton";
import TranslatableText from "@/components/messaging/TranslatableText";
import { useUserLocale } from "@/lib/i18n/useUserLocale";

const ConversationInner = () => {
  const { conversationId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { conversations } = useConversations();
  const { messages, loading, sending, send } = useMessages(conversationId || null);
  const { autoTranslate } = useUserLocale();
  const [draft, setDraft] = useState("");
  const scroller = useRef<HTMLDivElement>(null);

  const conv = useMemo(
    () => conversations.find((c) => c.id === conversationId),
    [conversations, conversationId],
  );

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages.length]);

  const onSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    const { error } = await send("text", { body: text });
    if (error) {
      setDraft(text);
      toast({ title: "Couldn't send", description: humanizeSendError(error), variant: "destructive" });
    }
  };

  return (
    <main className="mx-auto flex h-[calc(100vh-8rem)] max-w-2xl flex-col px-6 py-6">
      <header className="mb-4 flex items-center gap-3 border-b border-border pb-4">
        <Link
          to="/messages"
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-accent transition"
          aria-label="Back to messages"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="h-10 w-10 flex-none overflow-hidden rounded-full bg-muted">
          {conv?.other?.photos?.[0] && (
            <SignedImage
              src={conv.other.photos[0]}
              alt={conv.other.display_name ?? "Match"}
              className="h-full w-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">
            {conv?.other?.display_name ?? "Conversation"}
          </p>
          {conv?.other?.profile_id && (
            <Link
              to={`/u/${conv.other.profile_id}`}
              className="text-xs text-muted-foreground hover:underline"
            >
              View profile
            </Link>
          )}
        </div>
        {conv?.other?.id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Conversation options">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <ReportDialog
                targetUserId={conv.other.id}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Flag className="h-4 w-4 mr-2" /> Report
                  </DropdownMenuItem>
                }
              />
              <DropdownMenuSeparator />
              <BlockButton
                targetUserId={conv.other.id}
                targetName={conv.other.display_name ?? undefined}
                lastMessageId={
                  [...messages].reverse().find((m) => m.sender_id === conv.other?.id)?.id
                }
                lastMessageExcerpt={
                  [...messages].reverse().find((m) => m.sender_id === conv.other?.id)?.body ??
                    undefined
                }
                onBlocked={() => navigate("/messages")}
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="text-destructive focus:text-destructive"
                  >
                    <Ban className="h-4 w-4 mr-2" /> Block
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>
      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto pr-1">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No messages yet — say hello.
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                    mine
                      ? "bg-foreground text-background"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {m.kind === "text" ? (
                    mine ? (
                      <p className="whitespace-pre-wrap">{m.body}</p>
                    ) : (
                      <TranslatableText
                        text={m.body ?? ""}
                        autoTranslate={autoTranslate}
                      />
                    )
                  ) : m.kind === "voice" ? (
                    <audio controls src={m.media_url ?? ""} className="max-w-full" />
                  ) : m.kind === "image" ? (
                    <img
                      src={m.media_url ?? ""}
                      alt="Shared media"
                      className="max-h-64 rounded-lg"
                    />
                  ) : (
                    <p className="italic opacity-70">{m.body}</p>
                  )}
                  <p
                    className={`mt-1 text-[10px] ${
                      mine ? "text-background/70" : "text-muted-foreground"
                    }`}
                  >
                    {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <div className="flex items-end gap-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
            placeholder="Write a message…"
            className="min-h-[44px] flex-1 resize-none"
            rows={1}
            disabled={sending}
          />
          <Button onClick={onSend} disabled={sending || !draft.trim()} size="icon">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-1 flex justify-end">
          <RaelaCoPilotButton
            conversationId={conversationId || null}
            draft={draft}
            recentMessages={messages.slice(-8).map((m) => ({
              role: m.sender_id === user?.id ? "me" : "them",
              text: m.body ?? "",
            }))}
            onPick={(text) => setDraft(text)}
          />
        </div>
      </div>
    </main>
  );
};

const Conversation = () => (
  <OnboardingGate>
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <ConversationInner />
      <SiteFooter />
    </div>
  </OnboardingGate>
);

export default Conversation;
